import type { ReactNode } from 'react';
import type { DoorTypePresentation } from '../../appdata.js';

function CheckIcon({ className }: { className: string }) {
  return (
    <svg
      aria-hidden="true"
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}

/**
 * The native radio covers the card, keeping its single-choice state and
 * keyboard behavior accessible while leaving the price Retry control usable.
 */
export function DoorTypeCard({
  door,
  selected,
  price,
  onSelect,
}: {
  door: DoorTypePresentation;
  selected: boolean;
  /** Price status for the card, including any Retry control. */
  price: ReactNode;
  onSelect: () => void;
}) {
  const priceId = `door-type-${door.id}-price`;

  return (
    <article
      data-name={`door-type-card-${door.id}`}
      className={`relative flex flex-row overflow-hidden rounded-xl border-2 bg-white transition-shadow sm:flex-col ${
        selected
          ? 'border-brand shadow-lg'
          : 'border-gray-200 hover:border-gray-300 hover:shadow-md'
      }`}
    >
      {door.badge && (
        <span className="pointer-events-none absolute top-3 left-3 z-10 rounded-full bg-brand px-2 py-0.5 text-[9px] font-bold tracking-wider text-white uppercase">
          {door.badge}
        </span>
      )}

      <div
        className={`relative flex w-[42%] shrink-0 items-end justify-center px-3 pt-6 pb-3 sm:w-full sm:px-4 sm:pt-8 sm:pb-4 ${
          selected ? 'bg-white' : 'bg-gray-100'
        }`}
      >
        <img
          src={door.image.src}
          alt={door.image.alt}
          className="h-36 w-auto object-contain sm:h-56"
        />
        {selected && (
          <span
            data-name={`door-type-selected-${door.id}`}
            className="absolute right-2 bottom-2 flex size-6 items-center justify-center rounded-full bg-brand text-white shadow-sm"
          >
            <CheckIcon className="size-3.5" />
          </span>
        )}
      </div>

      <div className="flex min-w-0 flex-1 flex-col px-3 pt-3 pb-4 sm:px-4 sm:pt-4 sm:pb-5">
        <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between sm:gap-3">
          <div className="min-w-0">
            <h2 className="text-sm leading-tight font-semibold has-[:focus-visible]:rounded-sm has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-brand-strong has-[:focus-visible]:outline-offset-2 sm:text-base">
              <label>
                {/* Native radio appearance otherwise limits the card's clickable area. */}
                <input
                  type="radio"
                  name="door-type"
                  value={door.id}
                  data-name={`door-type-select-${door.id}`}
                  aria-describedby={priceId}
                  checked={selected}
                  onChange={onSelect}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      onSelect();
                    }
                  }}
                  className="absolute inset-0 z-1 h-full w-full cursor-pointer appearance-none opacity-0"
                />
                {door.title}
              </label>
            </h2>
            <p className="mt-1 text-xs leading-snug text-gray-600 sm:min-h-9">{door.subtitle}</p>
          </div>
          <div
            id={priceId}
            aria-live="polite"
            className="pointer-events-none relative z-10 shrink-0 text-sm sm:text-right"
          >
            {price}
          </div>
        </div>

        <ul className="mt-3 space-y-1.5 border-t border-gray-100 pt-3">
          {door.features.map((feature) => (
            <li key={feature} className="flex items-start gap-1.5 text-xs text-gray-700">
              <CheckIcon
                className={`mt-0.5 size-3 shrink-0 ${selected ? 'text-brand' : 'text-gray-400'}`}
              />
              {feature}
            </li>
          ))}
        </ul>
      </div>
    </article>
  );
}
